import { createSlice } from "@reduxjs/toolkit";
import {
  fetchBuyerInquiries,
  submitPartInquiry,
  markInquiryAsRead,
  markAllInquiriesAsRead,
  updateInquiryBells
} from "./partInquiryThunk";

const initialState = {
  inquiries: [],
  notifications: [], // All notifications from real-time listener
  isSubmitting: false,
  isLoading: false,
  isMarkingRead: false,
  updateLoading: false,
  error: null,
  successMessage: null,
  newNotificationCount: 0,

  // Unread count
  unreadCount: 0,
  isCountLoading: false
};

const partInquirySlice = createSlice({
  name: "partInquiry",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    resetNotifications: (state) => {
      state.notifications = [];
    },
    setNotifications: (state, action) => {
      state.notifications = action.payload;
      state.isLoading = false;
    },
    setNotificationsError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    setUnreadCount: (state, action) => {
      state.unreadCount = action.payload;
      state.isCountLoading = false;
    },
    setNotificationCount: (state, action) => {
      state.newNotificationCount = action.payload;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Submit inquiry
      .addCase(submitPartInquiry.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(submitPartInquiry.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.successMessage = action.payload.message;
      })
      .addCase(submitPartInquiry.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload || "Failed to submit inquiry";
      })

      .addCase(updateInquiryBells.pending, (state) => {
        state.updateLoading = true;
      })
      .addCase(updateInquiryBells.fulfilled, (state, action) => {
        state.updateLoading = false;
      })
      .addCase(updateInquiryBells.rejected, (state, action) => {
        state.updateLoading = false;
      })

      // Fetch inquiries
      .addCase(fetchBuyerInquiries.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBuyerInquiries.fulfilled, (state, action) => {
        state.isLoading = false;
        state.inquiries = action.payload;
      })
      .addCase(fetchBuyerInquiries.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch inquiries";
      })

      // Mark inquiry as read
      .addCase(markInquiryAsRead.pending, (state) => {
        state.isMarkingRead = true;
      })
      .addCase(markInquiryAsRead.fulfilled, (state, action) => {
        state.isMarkingRead = false;

        // Update local state optimistically (real-time will update too)
        const { buyerId, inquiryId, userId } = action.payload;

        state.notifications = state.notifications.map((notification) => {
          if (notification.buyerDetail.buyerId === buyerId) {
            return {
              ...notification,
              allInquiries: notification.allInquiries.map((inquiry) => {
                if (inquiry.inquiryId === inquiryId) {
                  const isReadArray = inquiry.isRead || [];
                  if (!isReadArray.includes(userId)) {
                    return {
                      ...inquiry,
                      isRead: [...isReadArray, userId]
                    };
                  }
                }
                return inquiry;
              })
            };
          }
          return notification;
        });

        // Decrement unread count optimistically
        if (state.unreadCount > 0) {
          state.unreadCount--;
        }
      })
      .addCase(markInquiryAsRead.rejected, (state, action) => {
        state.isMarkingRead = false;
        state.error = action.payload || "Failed to mark as read";
      })

      // Mark all as read
      .addCase(markAllInquiriesAsRead.pending, (state) => {
        state.isMarkingRead = true;
      })
      .addCase(markAllInquiriesAsRead.fulfilled, (state, action) => {
        state.isMarkingRead = false;

        const { userId } = action.payload;

        // Update all inquiries optimistically
        state.notifications = state.notifications.map((notification) => ({
          ...notification,
          allInquiries: notification.allInquiries.map((inquiry) => {
            const isReadArray = inquiry.isRead || [];
            if (!isReadArray.includes(userId)) {
              return {
                ...inquiry,
                isRead: [...isReadArray, userId]
              };
            }
            return inquiry;
          })
        }));

        // Reset unread count
        state.unreadCount = 0;
        state.successMessage = "All notifications marked as read";
      })
      .addCase(markAllInquiriesAsRead.rejected, (state, action) => {
        state.isMarkingRead = false;
        state.error = action.payload || "Failed to mark all as read";
      });
  }
});

export const {
  clearError,
  clearSuccessMessage,
  resetNotifications,
  setNotifications,
  setNotificationsError,
  notificationCount,
  setUnreadCount,
  setLoading
} = partInquirySlice.actions;

export default partInquirySlice.reducer;
